import authService from './authService';
import { ApiResponse } from '../types/common';
import { apiErrorFrom } from '../utils/apiError';
import {
  DemoBalance,
  DemoConfirmResult,
  DemoPendingCustomer,
  DemoReconciliationHistoryPage,
  DemoSelectionPayload,
  DemoWorkspace,
} from '../types/demoReconciliation';

/**
 * Conciliação de Demonstração.
 * Endpoint base: /api/v1/demo-reconciliation
 */
class DemoReconciliationService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /demo-reconciliation/customers?search= — clientes com demonstração pendente. */
  async getPendingCustomers(search?: string): Promise<DemoPendingCustomer[]> {
    const query = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/demo-reconciliation/customers${query}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao carregar clientes com demonstração pendente');
    const json: ApiResponse<DemoPendingCustomer[]> = await response.json();
    return json.data ?? [];
  }

  /** GET /demo-reconciliation/customers/:customerId — área de trabalho da cliente. */
  async getWorkspace(customerId: number): Promise<DemoWorkspace> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/demo-reconciliation/customers/${customerId}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao carregar a conciliação da cliente');
    const json: ApiResponse<DemoWorkspace> = await response.json();
    return json.data!;
  }

  /** POST /demo-reconciliation/customers/:customerId/preview — saldo calculado pelo backend. */
  async preview(customerId: number, payload: DemoSelectionPayload): Promise<DemoBalance> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/demo-reconciliation/customers/${customerId}/preview`, payload);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao calcular o saldo da cliente');
    const json: ApiResponse<DemoBalance> = await response.json();
    return json.data!;
  }

  /** POST /demo-reconciliation/customers/:customerId/confirm — gera a proposta ou encerra sem cobrança. */
  async confirm(customerId: number, payload: DemoSelectionPayload): Promise<DemoConfirmResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/demo-reconciliation/customers/${customerId}/confirm`, payload);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao confirmar a conciliação');
    const json: ApiResponse<DemoConfirmResult> = await response.json();
    return json.data!;
  }

  /** GET /demo-reconciliation?page=&limit= — histórico de conciliações. */
  async getHistory(page: number, limit: number): Promise<DemoReconciliationHistoryPage> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/demo-reconciliation?${params}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao carregar o histórico de conciliações');
    const json: ApiResponse<DemoReconciliationHistoryPage> = await response.json();
    return json.data ?? { items: [], total: 0, page, limit };
  }
}

export const demoReconciliationService = new DemoReconciliationService();
export default demoReconciliationService;
